/**
 * HTML/CSS do widget Sumário (table-of-contents) — preview e export alinhados.
 */
import * as LucideIcons from 'lucide-react';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { buildTocDisplayItems } from '../utils/tocRegistry';

const TOC_PRESETS = new Set(['classic', 'modern', 'minimal', 'formal', 'boxed', 'sidebar']);

/**
 * @param {string} raw
 * @returns {string}
 */
export function normalizeTocPreset(raw) {
  const p = String(raw || 'classic').trim();
  return TOC_PRESETS.has(p) ? p : 'classic';
}

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @returns {boolean}
 */
export function resolveTocLeadersEnabled(content) {
  const c = content || {};
  if (c.showLeaders === true) return true;
  if (c.showLeaders === false) return false;
  return normalizeTocPreset(c.tocPreset) === 'formal';
}

/**
 * @param {string} iconName
 * @param {number} [size=16]
 * @returns {string}
 */
function renderTocLucideIconSvg(iconName, size = 16) {
  const raw = String(iconName ?? '').trim();
  if (!raw) return '';
  const name = raw.replace(/^lucide:/i, '');
  const Comp = LucideIcons[name] || LucideIcons.Circle;
  return renderToStaticMarkup(React.createElement(Comp, { size, strokeWidth: 2 }));
}

/**
 * @param {string} raw
 * @returns {string}
 */
function safeCssColor(raw) {
  return String(raw || '').replace(/["'<>\\]/g, '').trim();
}

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @param {import('../utils/tocRegistry').TocRegistryEntry[]} availableEntries
 * @returns {string}
 */
export function buildTocInnerHtml(content, availableEntries, opts = {}) {
  const c = content || {};
  const preset = normalizeTocPreset(c.tocPreset);
  const showNumbers = c.showNumbers !== false;
  const showPageNumbers = c.showPageNumbers === true;
  const showIcons = c.showIcons === true;
  const showLeaders = resolveTocLeadersEnabled(c);
  const title = String(c.title || 'Sumário').trim() || 'Sumário';
  const fs = Math.min(28, Math.max(12, parseInt(c.fontSize, 10) || 15));
  const lh = Math.min(3, Math.max(1, Number(c.lineHeight) || 1.5));
  const itemGap = Math.min(24, Math.max(0, Number(c.itemGap) || 0));
  const linkColor = safeCssColor(c.linkColor);
  const items = buildTocDisplayItems(c, availableEntries || []);
  const emptyMsg = String(opts.emptyMessage || '').trim()
    || 'Nenhuma entrada no sumário. Marque «Incluir no sumário» em Título âncora, Cabeçalho ou Card estilizado.';

  const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const navStyleParts = [`font-size:${fs}px`, `line-height:${lh}`];
  if (itemGap > 0) navStyleParts.push(`--madri-toc-item-gap:${itemGap}px`);
  const navStyle = navStyleParts.join(';');
  const leaderCls = showLeaders ? ' madri-toc--leaders' : '';
  const linkStyleAttr = linkColor ? ` style="color:${linkColor}"` : '';

  if (items.length === 0) {
    return `<nav class="madri-toc madri-toc--${preset}${leaderCls} madri-toc--empty" aria-label="${esc(title)}" style="${navStyle}"><p class="madri-toc__empty">${esc(emptyMsg)}</p></nav>`;
  }

  let n = 0;
  const rows = items.map((item) => {
    if (showNumbers) n += 1;
    const safeFrag = String(item.anchorId || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80);
    const num = showNumbers ? `<span class="madri-toc__num">${n}.</span>` : '';
    const iconHtml = showIcons && item.displayIcon
      ? `<span class="madri-toc__icon" aria-hidden="true">${renderTocLucideIconSvg(item.displayIcon, Math.max(14, Math.min(22, fs - 1)))}</span>`
      : '';
    const prefixHtml = (num || iconHtml)
      ? `<span class="madri-toc__prefix">${num}${iconHtml}</span>`
      : '';
    const pageHtml = showPageNumbers && item.pageNumber
      ? `<span class="madri-toc__page">${item.pageNumber}</span>`
      : '';
    const lvl = item.displayLevel || 1;
    const onclk = safeFrag
      ? ` onclick="event.preventDefault();if(typeof madriScrollToAnchor==='function'){madriScrollToAnchor('${safeFrag}');}return false;"`
      : '';
    const href = safeFrag ? `#${safeFrag}` : '#';
    const linkHtml = `<a href="${href}" class="madri-toc__link"${linkStyleAttr}${onclk}>${esc(item.displayLabel)}</a>`;

    if (showLeaders) {
      const leaderHtml = `<span class="madri-toc__leader" aria-hidden="true"></span>`;
      return `<li class="madri-toc__item madri-toc__item--level-${lvl}">${prefixHtml}<span class="madri-toc__body">${linkHtml}${leaderHtml}${pageHtml}</span></li>`;
    }

    return `<li class="madri-toc__item madri-toc__item--level-${lvl}">${prefixHtml}${linkHtml}${pageHtml}</li>`;
  }).join('');

  const titleHtml = title
    ? `<div class="madri-toc__title">${esc(title)}</div>`
    : '';

  return `<nav class="madri-toc madri-toc--${preset}${leaderCls}" aria-label="${esc(title)}" style="${navStyle}">${titleHtml}<ol class="madri-toc__list">${rows}</ol></nav>`;
}
